import React, { useEffect, useState } from "react";

import { useParams } from "react-router-dom";
import { getCampaignParticipant } from "@api/Campaign/CampaignParticipants";
import UploadDeliverableContentModal from "@pages/campaigns/brief/components/UploadDeliverableContentModal";
import {
  ContentReviewAsset,
  ContentReviewContent,
  ContentReviewDeliverable,
  ParticipantContentReview,
} from "@types";
import LoadingSpinner from "@components/LoadingSpinner";

import {
  deleteContentReviewAsset,
  updateContentStatus,
} from "@api/creator_view";
import toast from "react-hot-toast";
import { CONTENT_REVIEW_STATUS } from "@pages/campaigns/content-review/statuses";
import UpdateUploadedAssetModal from "../../profile/content-review/UpdateUploadedAssetModal";
import { ParticipantRecord } from "types/creator-view";

import useAuth from "@hooks/useAuth";
import { Role } from "@constants/roles";
import { useMixpanelTracking } from "@hooks/useMixpanelTracking";
import { EVENTS } from "@utils/mixpanel_utilities";
import { trackStepInWorkflow } from "@utils/mixpanel_time";
import { DeliverableContentSection } from "../components/creator-view";

const { ADMIN_REVIEW } = CONTENT_REVIEW_STATUS;

interface ProfileContentReviewProps {
  participantRecord: ParticipantRecord;
}

export default function CreatorContentReview({
  participantRecord,
}: ProfileContentReviewProps) {
  const { participant_id } = useParams();

  const [
    submittingForReviewDeliverableId,
    setSubmittingForReviewDeliverableId,
  ] = useState(null);

  const [loading, setLoading] = useState(false);

  const [selectedRounds, setSelectedRounds] = useState({});

  const [participantContentReview, setParticipantContentReview] =
    useState<ParticipantContentReview>(null);

  const [uploadingContentForDeliverable, setUploadingContentForDeliverable] =
    useState<ContentReviewDeliverable>(null);

  const [uploadingAssets, setUploadingAssets] = useState(false);

  const { deliverables = [] } = participantContentReview || {};

  const [assetToUpdate, setAssetToUpdate] = useState<ContentReviewAsset>(null);

  const { hasRole } = useAuth();
  const { trackEvent } = useMixpanelTracking();

  useEffect(() => {
    const initialize = (participant: ParticipantRecord) => {
      const {
        content_review = { deliverables: [] },
        deliverables: participantDeliverables,
        campaign,
      } = participant;

      // Update the deliverables with shorturl from participantDeliverables
      if (content_review && content_review.deliverables) {
        const updatedDeliverables = content_review.deliverables.map(
          (deliverable) => {
            const participantDeliverable = participantDeliverables.find(
              ({ content_review_id }) => content_review_id === deliverable.id
            );

            if (participantDeliverable) {
              const { shorturl } = participantDeliverable;
              return { ...deliverable, shorturl };
            }

            return deliverable;
          }
        );

        // Update the content_review with the modified deliverables
        const updatedContentReview = {
          ...content_review,
          deliverables: updatedDeliverables,
        };

        setParticipantContentReview(
          updatedContentReview as ParticipantContentReview
        );
      }

      // Track content review page view
      if (campaign) {
        trackEvent(EVENTS.PAGE_VIEW.CONTENT_SUBMISSION, {
          campaign_id: campaign.id,
          campaign_name: campaign.title,
          participant_id,
          deliverables_count: participantDeliverables?.length || 0,
        });
      }
    };

    const loadParticipantForBrandView = async () => {
      setLoading(true);

      try {
        const participantResponse =
          await getCampaignParticipant(participant_id);
        initialize(participantResponse.data);
      } catch (error) {
        toast.error("Error loading content review data");
        trackEvent("Error", {
          error_type: "Content Review Load Error",
          participant_id: participant_id,
          error_message: error?.message || "Unknown error",
        });
      } finally {
        setLoading(false);
      }
    };

    if (hasRole(Role.INFLUENCER)) {
      initialize(participantRecord);
    } else {
      loadParticipantForBrandView();
    }
  }, []);

  const handleUpdateDeliverable = (
    deliverableId: number,
    content: ContentReviewContent
  ) => {
    const newDeliverables = deliverables.map((deliverable) => {
      if (deliverable.id === deliverableId) {
        return { ...deliverable, content };
      }

      return deliverable;
    });

    setParticipantContentReview({
      ...participantContentReview,
      deliverables: newDeliverables,
    });
  };

  const handleSubmitForReview = async (content: ContentReviewContent) => {
    setSubmittingForReviewDeliverableId(content.deliverable_id);

    const trackingParams = {
      campaign_id: participantRecord?.campaign?.id,
      campaign_name: participantRecord?.campaign?.title,
      participant_id,
      content_id: content.id,
    };

    try {
      await updateContentStatus(content.id, ADMIN_REVIEW);

      handleUpdateDeliverable(content.deliverable_id, {
        ...content,
        status: ADMIN_REVIEW,
      });

      // Track content submission event
      trackEvent(EVENTS.CAMPAIGN.CONTENT_SUBMITTED, {
        ...trackingParams,
        deliverable_id: content.deliverable_id,
      });

      // Track workflow step completion
      trackStepInWorkflow(
        "Campaign Content",
        "Submitted for Review",
        trackingParams
      );

      toast.success("Content submitted for review");
    } catch (error) {
      toast.error("Error submitting content for review");

      // Track failure
      trackEvent("Error", {
        ...trackingParams,
        error_type: "Content Submission Error",
        error_message: error?.message || "Unknown error",
      });
    } finally {
      setSubmittingForReviewDeliverableId(false);
    }
  };

  const handleDeleteAsset = async (
    assetId: number,
    roundId: number,
    deliverableId: number
  ) => {
    const trackingParams = {
      campaign_id: participantRecord?.campaign?.id,
      participant_id: participant_id,
      asset_id: assetId,
      deliverable_id: deliverableId,
    };

    try {
      await deleteContentReviewAsset(assetId);

      const { content } = deliverables.find(({ id }) => id === deliverableId);
      const { rounds } = content;
      const newRounds = rounds.map((round) => {
        if (round.id !== roundId) {
          return round;
        }

        const { assets = [] } = round;
        const newAssets = assets.filter((asset) => asset.id !== assetId);

        return {
          ...round,
          assets: newAssets,
        };
      });

      const newContent = {
        ...content,
        rounds: newRounds,
      };

      handleUpdateDeliverable(deliverableId, newContent);

      // Track asset deletion
      trackEvent("Content Asset Deleted", trackingParams);
    } catch (error) {
      toast.error("Error deleting asset");

      // Track failure
      trackEvent("Error", {
        error_type: "Asset Deletion Error",
        ...trackingParams,
        error_message: error?.message || "Unknown error",
      });
    }
  };

  const handleAssetUpdated = (asset: ContentReviewAsset, updates) => {
    const { deliverable_id } = asset;

    const { content } = deliverables.find(({ id }) => id === deliverable_id);
    const { rounds } = content;

    const newRounds = rounds.map((round) => {
      const { assets = [] } = round;
      const oldAsset = assets.find(({ id }) => id === asset.id);

      if (!oldAsset) {
        return round;
      }

      return {
        ...round,
        assets: assets.map((oldAsset) => {
          if (oldAsset.id === asset.id) {
            return {
              ...oldAsset,
              ...updates,
            };
          }

          return oldAsset;
        }),
      };
    });

    const newContent = {
      ...content,
      rounds: newRounds,
    };

    handleUpdateDeliverable(deliverable_id, newContent);

    // Track asset update
    trackEvent("Content Asset Updated", {
      campaign_id: participantRecord?.campaign?.id,
      participant_id,
      asset_id: asset.id,
      deliverable_id: asset.deliverable_id,
      update_type: Object.keys(updates).join(","),
    });
  };

  const handleUploadContentClick = (deliverable, uploadAssetsOnly = false) => {
    setUploadingContentForDeliverable(deliverable);

    setUploadingAssets(uploadAssetsOnly);
  };

  const handleRoundChange = (deliverableId, roundValue) => {
    setSelectedRounds({
      ...selectedRounds,
      [deliverableId]: roundValue,
    });
  };

  if (loading) {
    return <LoadingSpinner className="mt-[200px]" />;
  }

  return (
    <div
      className={`p-2 lg:p-8 ${hasRole(Role.INFLUENCER) ? "" : "pr-[40px]"}`}
    >
      {deliverables.length > 0 ? (
        <DeliverableContentSection
          deliverables={deliverables.sort(
            (a, b) =>
              new Date(b.updated_at).getTime() -
              new Date(a.updated_at).getTime()
          )}
          selectedRounds={selectedRounds}
          onRoundChange={handleRoundChange}
          onSubmitForReview={handleSubmitForReview}
          submittingForReviewDeliverableId={submittingForReviewDeliverableId}
          onUploadContentClick={handleUploadContentClick}
          onDeleteAsset={handleDeleteAsset}
          onAssetClick={setAssetToUpdate}
          campaignParticipant={participantContentReview}
        />
      ) : (
        <div className="text-center py-10">
          <h2 className="text-2xl font-semibold mb-4">No Deliverables Found</h2>
          <p className="text-default_weak">
            There are no deliverables assigned for this campaign.
          </p>
        </div>
      )}

      {uploadingContentForDeliverable ? (
        <UploadDeliverableContentModal
          isUploadingAssets={uploadingAssets}
          deliverable={uploadingContentForDeliverable}
          onClose={() => setUploadingContentForDeliverable(null)}
          onContentUpdated={(content) => {
            handleUpdateDeliverable(uploadingContentForDeliverable.id, content);

            setUploadingContentForDeliverable(null);
          }}
        />
      ) : null}
      {assetToUpdate ? (
        <UpdateUploadedAssetModal
          asset={assetToUpdate}
          onClose={() => setAssetToUpdate(null)}
          onUpdate={(updates) => {
            handleAssetUpdated(assetToUpdate, updates);

            setAssetToUpdate(null);
          }}
        />
      ) : null}
    </div>
  );
}
